import React, { useEffect, useState, useContext } from "react";
import { Table, Spinner, Alert, Container, Card, Button } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
const CDRDetails = () => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [summaryData, setSummaryData] = useState([]);
    const [callFlowData, setCallFlowData] = useState([]);
    const [inboundData, setInboundData] = useState([]);
    const [outboundData, setOutboundData] = useState([]);
    const [channelData, setChannelData] = useState([]);
    const [variablesData, setVariablesData] = useState([]);
    const [applicationLogData, setApplicationLogData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const summaryColumns = [
        { key: "direction", label: "Direction" },
        { key: "name", label: "Name" },
        { key: "number", label: "Number" },
        { key: "destination", label: "Destination" },
        { key: "start", label: "Start" },
        { key: "end", label: "End" },
        { key: "hangup_cause", label: "Hangup Cause"},
        { key: "duration", label: "Duration" },
        { key: "status", label: "Status" },
    ];

    const callFlowColumns = [
        { key: "application", label: "Application" },
        { key: "destination_number", label: "Destination" },
        { key: "destination_name", label: "Name" },
        { key: "start", label: "Start" },
        { key: "end", label: "End" },
        { key: "duration", label: "Duration" },
        { key: "status", label: "Status" },
    ];

    const channelColumns = [
        { key: "name", label: "Name" },
        { key: "value", label: "Value" },
    ];

    const variablesColumns = [
        { key: "name", label: "Name" },
        { key: "value", label: "Value" },
    ];

    const applicationLogColumns = [
        { key: "name", label: "Name" },
        { key: "data", label: "Data" },
    ];

    const fetchCDRDetails = async () => {
        setLoading(true);
        try {
            let response = await fetch(
                `${serverHost}/fetch_variables/${encodeURIComponent(id)}`
            );
            if (!response.ok) throw new Error("Failed to fetch CDR details");
            let data = await response.json();
            console.log(data)
            setVariablesData(data.variables || []);
            setInboundData(data.inbound || []);
            setOutboundData(data.outbound || []);
            setApplicationLogData(data.applog || []);
            setChannelData(data.channel_data || [])
            
            response = await fetch(`${serverHost}/fetch_cf_summary/${encodeURIComponent(id)}`)
            if (!response.ok) throw new Error("Failed to fetch CDR details");
            data = await response.json();
            console.log(data.result)
            setCallFlowData(data.result || [])

            response = await fetch(`${serverHost}/fetch_cdr_summary/${encodeURIComponent(id)}`)
            if (!response.ok) throw new Error("Failed to fetch CDR details");
            data = await response.json();
            console.log(data.result)
            setSummaryData(data.result || [])
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCDRDetails();
    }, [id]);

    const renderTable = (title, columns, data) => (
        <Card className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light" : ""}`}>
            <Card.Header
                style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                }}
                className="d-flex justify-content-between align-items-center"
            >
                <h5 className="mb-0">{title}</h5>
            </Card.Header>
            <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                <div style={{ overflowX: "auto" }}>
                    <Table
                        striped
                        hover
                        responsive
                        bordered
                        className="mb-0"
                        style={{
                            minWidth: "600px",
                            backgroundColor: isDark ? "#1f2937" : "#fff",
                            color: isDark ? "#e2e8f0" : "#212529"
                        }}
                    >
                        <thead
                            style={{
                                backgroundColor: isDark ? "#1f2937" : "#fff",
                                fontWeight: "bold",
                                color: isDark ? "#e2e8f0" : "#212529",
                            }}
                        >
                            <tr>
                                {columns.map((col) => (
                                    <th key={col.key} className="text-center">
                                        {col.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.length > 0 ? (
                                data.map((row, index) => (
                                    <tr key={index} style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                        {columns.map((col) => (
                                            <td key={`${index}-${col.key}`} className="text-center">
                                                <span style={{ fontWeight: 400 }}>
                                                    {row[col.key] ?? "-"}
                                                </span>
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={columns.length}
                                        className="text-center"
                                        style={{ color: isDark ? "#e2e8f0" : "#212529" }}
                                    >
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    );

    if (loading)
        return (
            <div className="text-center mt-5">
                <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                    Loading call detail record...
                </p>
            </div>
        );

    if (error)
        return (
            <Alert variant="danger" className="mt-5 text-center">
                {error}
            </Alert>
        );

    return (
        <>
            <SimpleHeader name="Call Detail Record" parentName="HUCS" />
            <Container fluid className="mt--6">
                <div className="mb-3">
                    <Button 
                        variant="secondary" 
                        size="sm" 
                        onClick={() => navigate('/admin/hucs/call-detail-record')}
                        style={{
                            backgroundColor: isDark ? "#6c757d" : "#b3c3d1ff",
                            borderColor: isDark ? "#6c757d" : "#b3c3d1ff",
                        }}
                    >
                        ← Back to CDR List
                    </Button>
                </div>

                {renderTable("Summary", summaryColumns, summaryData)}
                {(callFlowData[0] !== "none" && callFlowData.length != 0) && renderTable("Call Flow Summary", callFlowColumns, callFlowData)}
                {(inboundData[0] !== "none" && inboundData.length != 0) && renderTable("Call Stats: Audio: inbound ", variablesColumns, inboundData)}
                {(outboundData[0] !== "none" && outboundData.length != 0) && renderTable("Call Stats: Audio: outbound", variablesColumns, outboundData)}
                {renderTable("Channel Data", channelColumns, channelData)}
                {renderTable("Variables", variablesColumns, variablesData)}
                {renderTable("Application Log", applicationLogColumns, applicationLogData)}
            </Container>
        </>
    );
};

export default CDRDetails;